import { DateTime } from 'luxon';
import { findTeamByLabel } from '../data/teams';

const SCOREBOARDS = {
  nfl: { path: 'nfl', params: {} },
  cfb: { path: 'college-football', params: { groups: '80', limit: '300' } },
};

const resolveTeamId = (teamId, label) => teamId || findTeamByLabel(label)?.id || '';

const parseTeamId = (teamId) => {
  const [league, espnId] = teamId.split('-');
  return SCOREBOARDS[league] && espnId ? { league, espnId } : null;
};

// ESPN groups scoreboard days by US Eastern date, so an 8:15pm ET kickoff stays on that day.
const scoreboardDate = (dateTime) => (
  DateTime.fromISO(dateTime).setZone('America/New_York').toFormat('yyyyLLdd')
);

const getScoreboardKey = (game) => {
  const home = parseTeamId(resolveTeamId(game.homeTeamId, game.homeTeam));
  const away = parseTeamId(resolveTeamId(game.awayTeamId, game.awayTeam));
  if (!home || !away || home.league !== away.league || !game.dateTime) return null;
  return {
    league: home.league,
    date: scoreboardDate(game.dateTime),
    homeId: home.espnId,
    awayId: away.espnId,
  };
};

const REQUEST_TIMEOUT_MS = 10000;
const GAME_STATES = ['pre', 'in', 'post'];

const fetchScoreboard = async (league, date) => {
  const { path, params } = SCOREBOARDS[league];
  const query = new URLSearchParams({ ...params, dates: date });
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    const response = await fetch(
      `https://site.api.espn.com/apis/site/v2/sports/football/${path}/scoreboard?${query}`,
      { signal: controller.signal },
    );
    if (!response.ok) throw new Error(`ESPN scoreboard returned ${response.status}`);
    const data = await response.json();
    return Array.isArray(data?.events) ? data.events : [];
  } finally {
    clearTimeout(timeout);
  }
};

const isScore = (value) => value !== '' && value != null && !Number.isNaN(Number(value));

const toLiveScore = (event, { homeId, awayId }) => {
  const status = event.status?.type;
  const competitors = event.competitions?.[0]?.competitors || [];
  const scoreFor = (espnId) => competitors.find((competitor) => competitor?.team?.id === espnId)?.score;
  const homeScore = scoreFor(homeId);
  const awayScore = scoreFor(awayId);
  if (!GAME_STATES.includes(status?.state) || !status.shortDetail || !isScore(homeScore) || !isScore(awayScore)) {
    return null;
  }
  return {
    state: status.state,
    detail: status.shortDetail,
    homeScore,
    awayScore,
  };
};

// Matches on team IDs rather than ESPN's home/away so neutral-site games still line up.
const findEvent = (events, { homeId, awayId }) => events.find((event) => {
  const ids = (event?.competitions?.[0]?.competitors || []).map((competitor) => competitor?.team?.id);
  return ids.includes(homeId) && ids.includes(awayId);
});

export const fetchLiveScores = async (games) => {
  const keyed = games
    .map((game) => ({ game, key: getScoreboardKey(game) }))
    .filter(({ key }) => key);

  const scoreboards = {};
  keyed.forEach(({ key }) => {
    scoreboards[`${key.league}:${key.date}`] = key;
  });

  const events = {};
  await Promise.all(Object.entries(scoreboards).map(async ([id, { league, date }]) => {
    try {
      events[id] = await fetchScoreboard(league, date);
    } catch (error) {
      console.error(`Could not load ${league} scores for ${date}:`, error);
      events[id] = [];
    }
  }));

  const scores = {};
  keyed.forEach(({ game, key }) => {
    const event = findEvent(events[`${key.league}:${key.date}`], key);
    const score = event ? toLiveScore(event, key) : null;
    if (score) scores[game.id] = score;
  });
  return scores;
};
